<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import { gaiaNeutral, gaiaTextButtonOverrides } from '../themes/tokens';

const props = defineProps({
  /** Muted note on the left, e.g. "Every section stays editable after creation". */
  note: {
    type: String,
    default: undefined,
  },
  /** Text-button on the left of the actions, e.g. "Cancel" or "Save draft". */
  quietLabel: {
    type: String,
    default: undefined,
  },
  /** Outline Back button; omit on the first step. */
  backLabel: {
    type: String,
    default: undefined,
  },
  nextLabel: {
    type: String,
    default: 'Continue',
  },
  /** Spinner in the primary button while the step is saving. */
  loading: {
    type: Boolean,
    default: false,
  },
  nextDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Attributes forwarded onto the Back and primary buttons. The bar owns both
   * buttons, so a caller that needs a handle on one of them — a `data-tour`
   * key for the guided demos, an `aria-describedby` — has no element of its
   * own to put it on.
   */
  backAttrs: {
    type: Object as PropType<Record<string, string>>,
    default: undefined,
  },
  nextAttrs: {
    type: Object as PropType<Record<string, string>>,
    default: undefined,
  },
});

const emit = defineEmits<{ quiet: []; back: []; next: [] }>();

const border = gaiaNeutral.borderMuted;
const bg = gaiaNeutral.surface;
</script>

<template>
  <!-- The sticky footer both wizards share (Create playbook, Prepare
       signature): a muted note left, then the quiet action, Back, and the
       primary CTA. The bar's own chrome is the only CSS — everything in it is
       a GaiaButton. -->
  <n-flex align="center" :size="10" :wrap="false" class="gaia-wizard-footer">
    <!-- The left side is a note, or whatever the caller slots there — the
         drafting wizard puts its autosave state here, which is a component
         rather than a string. Truncates rather than pushing the actions off
         the row. -->
    <slot name="note">
      <gaia-text v-if="props.note" truncate size="control" tone="subtle">
        {{ props.note }}
      </gaia-text>
    </slot>
    <n-flex align="center" :size="8" :wrap="false" :style="{ marginLeft: 'auto', flex: 'none' }">
      <gaia-button
        v-if="props.quietLabel"
        text
        :theme-overrides="gaiaTextButtonOverrides.ink"
        @click="emit('quiet')"
      >
        {{ props.quietLabel }}
      </gaia-button>
      <gaia-button v-if="props.backLabel" v-bind="props.backAttrs" @click="emit('back')">
        {{ props.backLabel }}
      </gaia-button>
      <!-- The primary button, wrapped so it can explain itself while it is
           off: a disabled <button> fires no mouse events, so a tooltip bound
           to the button would never open in exactly the state that needs
           explaining. The span is the hover target instead. With no
           `next-tooltip` slot the bubble stays shut and nothing changes. -->
      <gaia-tooltip placement="top-end" :disabled="!$slots['next-tooltip']">
        <template #trigger>
          <span class="gaia-wizard-footer__next">
            <!-- `next` swaps in a richer primary control (the drafting wizard's
                 split Finish button); it keeps the explaining tooltip. -->
            <slot name="next">
              <gaia-button
                v-bind="props.nextAttrs"
                type="primary"
                :loading="props.loading"
                :disabled="props.nextDisabled"
                @click="emit('next')"
              >
                {{ props.nextLabel }}
              </gaia-button>
            </slot>
          </span>
        </template>
        <slot name="next-tooltip" />
      </gaia-tooltip>
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* Chrome only: the rule above the bar and its padding. */
.gaia-wizard-footer {
  flex: none;
  padding: 14px 44px;
  border-top: 1px solid v-bind(border);
  background: v-bind(bg);
}

/* Hover target for the primary button's explanation — inline-flex so wrapping
   the button changes nothing about how the row lays out. */
.gaia-wizard-footer__next {
  display: inline-flex;
}
</style>
